import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", packageName: "" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "failed", packageName: "" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.payment_status === "paid") {
          setState({ phase: "paid", packageName: data.package_name || "your suite" });
          return;
        }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setState({ phase: "failed", packageName: "" });
    };
    poll();
  }, [sessionId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-xl shadow-navy/5" data-testid="payment-success-page">
      {state.phase === "checking" && (
        <div className="flex flex-col items-start gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-teal-600" aria-hidden="true" />
          <p className="font-display text-xl font-bold text-navy">Confirming your payment…</p>
          <p className="text-slate-600" data-testid="payment-status-text">This takes a few seconds.</p>
        </div>
      )}
      {state.phase === "paid" && (
        <div className="flex flex-col items-start gap-4">
          <CheckCircle2 className="h-10 w-10 text-teal-600" aria-hidden="true" />
          <p className="font-display text-2xl font-bold text-navy">Payment received — welcome to VeraChart.</p>
          <p className="text-slate-600" data-testid="payment-status-text">
            Your purchase of {state.packageName} is confirmed, and Stripe has emailed your receipt. Two quick next
            steps:
          </p>
          <ol className="list-decimal space-y-2 pl-6 text-slate-600">
            <li>Complete the onboarding questionnaire so we can customize your prompts (terminology, note format, required verbiage).</li>
            <li>We'll email you to schedule your 1-hour live setup and training session.</li>
          </ol>
          <Link
            to="/onboarding"
            data-testid="payment-success-onboarding-link"
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-teal-600 px-8 py-3.5 font-display text-base font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-teal-700"
          >
            Complete the Onboarding Questionnaire <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      )}
      {state.phase === "failed" && (
        <div className="flex flex-col items-start gap-4">
          <XCircle className="h-10 w-10 text-red-500" aria-hidden="true" />
          <p className="font-display text-xl font-bold text-navy">We couldn't confirm your payment yet.</p>
          <p className="text-slate-600" data-testid="payment-status-text">
            If you completed checkout, your receipt will arrive by email and no further action is needed. Otherwise
            contact <a href="mailto:sales@verachart.org" className="font-medium text-teal-700 underline underline-offset-2">sales@verachart.org</a>.
          </p>
        </div>
      )}
    </div>
  );
}

function Cancel() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-xl shadow-navy/5" data-testid="payment-cancel-page">
      <p className="font-display text-2xl font-bold text-navy">Checkout canceled — no charge was made.</p>
      <p className="mt-3 text-slate-600">
        You can pick up where you left off anytime, or talk to us first — no pressure.
      </p>
      <div className="mt-6 flex flex-wrap gap-4">
        <Link to="/" data-testid="payment-cancel-back-link" className="inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-teal-700">
          Back to Pricing <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <a href="mailto:sales@verachart.org" className="inline-flex items-center rounded-full border border-slate-300 px-7 py-3 font-display text-sm font-semibold text-navy transition-colors hover:border-teal-600 hover:text-teal-700">
          Email sales@verachart.org
        </a>
      </div>
    </div>
  );
}

export default function PaymentResult({ kind }) {
  useEffect(() => {
    document.title = kind === "success" ? "Payment Confirmed | VeraChart" : "Checkout Canceled | VeraChart";
    window.scrollTo(0, 0);
    return () => { document.title = "VeraChart | AI Prompts for Behavioral Health Documentation"; };
  }, [kind]);

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center px-4 sm:px-6 lg:px-8">
          <Logo />
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        {kind === "success" ? <Success /> : <Cancel />}
      </main>
      <footer className="border-t border-slate-200 bg-white py-8">
        <p className="mx-auto max-w-2xl px-4 text-xs text-slate-400 sm:px-6 lg:px-8">© 2026 VeraChart. All rights reserved.</p>
      </footer>
    </div>
  );
}
